<template>
  <div class="history-modal">
    <div class="columns">
      <div class="column col-3">
        <label class="form-label">Marcador</label>
        <select class="form-select"
                v-model="form.markerId">
          <option :value="null">[Selecione]</option>
          <option v-for="(item, i) in markers"
                  :value="item.id" :key="i">{{ item.name }}</option>
        </select>
      </div>
      <div class="column col-5">
        <label class="form-label">Sala</label>
        <select class="form-select"
                v-model="form.placeId">
          <option :value="null">[Selecione]</option>
          <option v-for="(item, i) in places"
                  :value="item.id" :key="i">{{ item.name }}</option>
        </select>
      </div>
    </div>
    <div class="divider" />
    <div class="stenci-title mt-2">
      <span>Solicitante / Indicação </span>
      <router-link to="/indications">[<small>Gerenciar solicitantes</small>]</router-link>
    </div>
    <div class="columns">
      <div
        class="column col-6 col-md-12 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.type.$error}"
      >
        <label class="form-label">Tipo</label>
        <select id="indicated-type"
                class="form-select"
                v-model="form.indicated.type"
                :disabled="requestRequired || hasProfessionalIndicated"
                @change="unsetIndicated(true)"
                @blur="$v.form.indicated.type.$touch()">
          <option value="">[Selecione]</option>
          <option value="collaborator">Colaborador</option>
          <option value="patient">Paciente</option>
          <option value="partner">Clínica/Laboratório</option>
          <option value="professional">Profissional da saúde</option>
        </select>
        <template v-if="$v.form.indicated.type.$error">
          <div class="form-input-hint"
               v-if="!$v.form.indicated.type.required">Campo obrigatório</div>
        </template>
      </div>

      <div
        class="column col-2 col-md-4 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.council.name.$error}"
        v-if="form.indicated.type === 'professional'"
      >
        <label class="form-label">Conselho</label>
        <select id="indicated-council" class="form-select"
                @blur="$v.form.indicated.council.name.$touch()"
                v-model="form.indicated.council.name"
                :disabled="hasProfessionalIndicated"
        >
          <option value="">[Selecione]</option>
          <option v-for="(text, value) in council.councils"
                  :value="value" :key="value">{{ value }}</option>
        </select>
      </div>
      <div
        class="column col-2 col-md-4 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.council.state.$error}"
        v-if="form.indicated.type === 'professional'"
      >
        <label class="form-label">UF</label>
        <select id="indicated-state" class="form-select"
                @blur="$v.form.indicated.council.state.$touch()"
                :disabled="hasProfessionalIndicated"
                v-model="form.indicated.council.state">
          <option value="">[Selecione]</option>
          <option v-for="(text, value) in states"
                  :value="value" :key="value">{{ value }}</option>
        </select>
      </div>
      <div
        class="column col-2 col-md-4 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.council.record.$error}"
        v-if="form.indicated.type === 'professional'"
      >
        <label for="indicated-record" class="form-label">Número</label>
        <div class="input-group">
          <input type="text" id="indicated-record"
                 class="form-input" placeholder="00000"
                 @blur="$v.form.indicated.council.record.$touch()"
                 @change="findIndicatedByCouncil"
                 v-model="form.indicated.council.record"
                 :disabled="notCouncil || hasProfessionalIndicated || loadingIndicated"
          />
          <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                  :class="{loading: loadingIndicated}"
                  tabindex="-1" v-if="!hasProfessionalIndicated">
            <fa-icon :icon="['fal', 'search']"/>
          </button>
          <button slot="action"
                  @click="unsetIndicated"
                  class="btn btn-action input-group-btn btn-icon btn-gray"
                  tabindex="-1"
                  v-else
          >
            <fa-icon :icon="['fal', 'times']">
            </fa-icon>
          </button>
        </div>
      </div>
      <div
        class="column col-6 col-md-12 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.name.$error}"
      >
        <label class="form-label">Nome</label>
        <dx-autocomplete
          id="indicated-name"
          v-model="indicated"
          :source="findIndicated"
          label="name"
          track-by="id"
          :readonly="!!form.indicated.id"
          :disabled="notCouncil"
          @select="setIndicated"
          @blur="onIndicatedBlur"
          :debounce="800"
          placeholder="Nome, CPF ou CRM">
          <template slot="action">
            <button
              @click="unsetIndicated"
              class="btn btn-action input-group-btn btn-icon"
              :class="form.indicated ? 'btn-gray' : 'btn-neutral'"
              :disabled="notCouncil || hasProfessionalIndicated"
              tabindex="-1">
              <fa-icon :icon="['fal', form.indicated ? 'times' : 'search']"></fa-icon>
            </button>
          </template>
          <template v-slot="{ item }">
            <div>{{ item.name }}</div>
            <small class="text-primary" v-if="item.id === null">
              (indicado sem cadastro)
            </small>
          </template>
        </dx-autocomplete>
      </div>
      <div
        class="column col-6 col-md-12 col-sm-12 form-group"
        :class="{'has-error': $v.form.indicated.specialty.code.$error}"
        v-if="form.indicated.type === 'professional'"
      >
        <label class="form-label">Especialidade</label>
        <dx-autocomplete
          v-model="specialty"
          :source="findSpecialty"
          label="name"
          track-by="id"
          :readonly="!!form.indicated.specialty.code"
          :disabled="notCouncil"
          :debounce="800"
          @select="addSpecialty"
          @blur="$v.form.indicated.specialty.code.$touch()"
          placeholder="Informe o código ou nome..."
          input-id="expense-name">
          <button slot="action" @click="unsetSpecialty"
                  class="btn btn-action input-group-btn btn-icon btn-gray"
                  :disabled="notCouncil || hasProfessionalIndicated"
                  tabindex="-1">
            <fa-icon :icon="['fal', 'times']">
            </fa-icon>
          </button>
          <template v-slot="{ item }">
            <a>
              <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
              <span class="ml-1">
                {{ item.code }} - {{ item.name }}
              </span>
            </a>
          </template>
        </dx-autocomplete>
      </div>
    </div>
    <div class="divider" />
    <div class="stenci-title">Histórico de agendamentos</div>
    <template v-if="!uploadedAppointments">
      <div class="empty mt-2">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h6">Histórico de agendamentos</p>
        <p class="empty-subtitle">
          Clique no botão abaixo para carregar o histórico de agendamentos do paciente
        </p>
        <button
          class="btn btn-primary btn-icon btn-icon-left"
          :class="{loading}"
          :disabled="loading"
          @click="loadAppointments"
        ><fa-icon :icon="['fal', 'sync']"/>Carregar histórico</button>
      </div>
    </template>
    <template v-else>
      <div class="empty mt-2" v-if="data.items && data.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h6">Histórico de agendamentos</p>
        <p class="empty-subtitle">
          Não foram encontrados outros agendamentos para este paciente.
        </p>
      </div>
      <template v-else>
        <table class="table table-hover">
          <thead>
          <tr>
            <th>Atendimento</th>
            <th>Profissional</th>
            <th>Convênio</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, i) in data.items" :key="i">
            <td style="width:200px">
              <div>
                {{ item.date | date('DD/MM/YYYY') }}
                <small>({{ dateFromNow(item.date) }})</small>
              </div>
              <div class="d-flex mt-1 h-centered">
                <fa-icon
                  :icon="['fas', 'stop']"
                  :style="{ color: status.getColor(item.status) }"
                ></fa-icon>
                <small class="ml-1">{{ status.getName(item.status) }}</small>
              </div>
            </td>
            <td>
              <div>{{ item.professional.name | capitalizeName }}</div>
              <div><small>{{ item.professional.specialty }}</small></div>
            </td>
            <td>
              <div>{{ item.insurance.name }}</div>
              <div v-if="item.expenses && item.expenses.length > 0">
                <small class="text-info text-bold" v-if="item.expenses[0].return">
                  [Retorno]
                </small>
                <small>{{ item.expenses[0].expense.name }}</small>
                <small class="ml-1 label text-secondary hover-link ml-1"
                       @click="openModalExpenses(item.expenses)"
                       v-if="item.expenses.length > 1">
                  +{{ item.expenses.length -1 }}
                </small>
              </div>
              <small v-else>[Procedimento não informado]</small>
            </td>
            <td class="text-right">
              <div class="loading" v-if="detail.loading && i === idx" />
              <fa-icon
                class="c-hand"
                :icon="['fal', 'search']"
                @click="openDetail(item, i)"
                v-else />
            </td>
          </tr>
          </tbody>
        </table>
        <div class="load-more" v-if="data.items.length > 0">
          <button
            v-if="data.hasMore && !loading"
            class="btn btn-primary"
            @click="loadMore"
            :disabled="loadingMore"
            :class="{ loading: loadingMore }"
          >Carregar mais...</button>
        </div>
      </template>
    </template>
    <dx-modal title="Detalhes"
              v-if="detail.show"
              v-model="detail.show"
              id="modal-history-detail">
      <table class="table table-striped">
        <thead>
        <tr>
          <th>Data</th>
          <th>Usuário</th>
          <th>Status</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(event, i) in detail.data.events" :key="i">
          <td>{{ event.at | date('DD/MM/YYYY HH:mm') }}</td>
          <td>{{ event.by.name || '--' }}</td>
          <td>{{ eventDescription(event) }}</td>
        </tr>
        </tbody>
      </table>
      <template slot="footer">
        <button class="btn" @click="detail.show = false">Sair</button>
      </template>
    </dx-modal>
    <dx-modal title="Despesas"
              v-if="expenseModal.show"
              v-model="expenseModal.show"
              id="modal-expense-detail">
      <table class="table table-striped">
        <thead>
        <tr>
          <th>Código</th>
          <th>Descrição</th>
          <th class="text-center">Quantidade</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(expense, i) in expenseModal.items" :key="i">
          <td>{{ expense.expense.code | tuss }}</td>
          <td>{{ expense.expense.name }}</td>
          <td class="text-center">{{ expense.quantity }}</td>
        </tr>
        </tbody>
      </table>
      <template slot="footer">
        <button class="btn" @click="expenseModal.show = false">Sair</button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import * as status from 'src/data/appointment-statuses';
import * as council from 'src/data/council-types';
import states from 'src/data/states';
import {
  required,
} from 'vuelidate/lib/validators';
import { capitalizeWord as capitalize } from '@/filters/capitalize';

export default {
  props: {
    patientId: {
      type: String,
    },
  },
  data() {
    return {
      status,
      council,
      states,
      markers: [],
      places: [],
      data: {
        items: [],
        hasMore: false,
        limit: 15,
        offset: 0,
      },
      uploadedAppointments: false,
      loading: false,
      loadingMore: false,
      loadingIndicated: false,
      idx: '',
      indicated: null,
      specialty: null,
      detail: {
        loading: false,
        data: Object,
        collaborators: [],
        show: false,
      },
      expenseModal: {
        show: false,
        items: [],
      },
    };
  },
  mounted() {
    this.loadMarkers();
    this.loadPlaces();

    if (this.requestRequired) {
      this.form.indicated.type = 'professional';
    }
    if (this.form.indicated.name) {
      this.indicated = {
        id: this.form.indicated.id,
        name: this.form.indicated.name,
      };
    }
    if (this.form.indicated.specialty.code) {
      this.specialty = this.form.indicated.specialty;
    }
  },
  validations() {
    const rules = {
      form: {
        indicated: {
          name: {},
          type: {},
          council: {
            name: {},
            state: {},
            record: {},
          },
          specialty: {
            code: {},
          },
        },
      },
    };

    if (this.requestRequired
      || (this.form.indicated.council.state
        && this.form.indicated.council.name
        && this.form.indicated.council.record)
    ) {
      rules.form.indicated.name = { required };
      rules.form.indicated.type = { required };
      if (this.form.indicated.type === 'professional') {
        rules.form.indicated.council = {
          name: { required },
          state: { required },
          record: { required },
        };
        rules.form.indicated.specialty = {
          code: { required },
        };
      }
    }

    return rules;
  },
  computed: {
    ...mapState({
      professionals: state => state.appointment.professionals,
      form: state => state.appointment.form,
    }),
    ...mapGetters({
      branchSettings: 'branchSettings',
    }),
    requestRequired() {
      const isExam = this.form.expenses
        .some(({ expense }) => expense.type === 'exam');
      return !!this.branchSettings['clinical.requestProfessionalRequired'] && isExam;
    },
    notCouncil() {
      return this.form.indicated.type === 'professional'
        && !(this.form.indicated.council.name && this.form.indicated.council.state);
    },
    hasProfessionalIndicated() {
      return !!this.form.indicated.id && this.form.indicated.type === 'professional';
    },
  },
  methods: {
    loadAppointments() {
      if (!this.patientId) {
        return null;
      }

      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      return this.$http
        .get(`/patients/${this.patientId}/appointments`, { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
          this.uploadedAppointments = true;
        });
    },
    loadCollaborators() {
      this.detail.loading = true;

      const params = {
        limit: 0,
      };

      return this.$http.get('/collaborators', { params })
        .then(({ data }) => {
          this.detail.collaborators = data.items;
        })
        .catch(() => {})
        .then(() => {
          this.detail.loading = false;
        });
    },
    loadMarkers() {
      const params = {
        active: true,
      };
      return this.$http.get('/markers', { params })
        .then(({ data }) => {
          this.markers = data.items;
        })
        .catch(() => {});
    },
    loadPlaces() {
      const params = {
        active: true,
        type: 'clinical',
      };
      return this.$http.get('/places', { params })
        .then(({ data }) => {
          this.places = data.items;
        })
        .catch(() => {});
    },
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.loadAppointments();
    },
    findIndicated(search) {
      return this.$http.get(`/appointment-indications?search=${search}`)
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{ id: null, name: search }];
          }
          return data.items;
        });
    },
    findIndicatedByCouncil() {
      if (!this.form.indicated.council.name
        && !this.form.indicated.council.state
        && !this.form.indicated.council.record
      ) {
        return null;
      }

      this.loadingIndicated = true;

      const params = {
        councilName: this.form.indicated.council.name,
        councilState: this.form.indicated.council.state,
        councilRecord: this.form.indicated.council.record,
      };

      return this.$http.get('/appointment-indications', { params })
        .then(({ data }) => {
          if (data?.items.length > 0) {
            this.setIndicated(data.items[0]);
          }
        })
        .finally(() => {
          this.loadingIndicated = false;
        });
    },
    setIndicated(data) {
      this.indicated = {
        id: data.id,
        name: data.name,
      };
      this.specialty = data.specialty;
      this.form.indicated.id = data.id;
      this.form.indicated.name = data.name;
      if (data.type) {
        this.form.indicated.type = data.type;
      }
      if (data.council && data.council.name) {
        this.form.indicated.council.name = data.council.name;
      }
      if (data.council && data.council.state) {
        this.form.indicated.council.state = data.council.state;
      }
      if (data.council && data.council.record) {
        this.form.indicated.council.record = data.council.record;
      }
      if (data.specialty && data.specialty.code) {
        this.form.indicated.specialty = data.specialty;
      }
    },
    onIndicatedBlur(text) {
      this.form.indicated.name = text;
      this.$v.form.indicated.name.$touch();
    },
    unsetIndicated(preserveType) {
      const { type } = this.form.indicated;

      this.form.indicated = this.blankIndicatedForm();
      this.indicated = null;

      this.unsetSpecialty();
      if (preserveType) {
        this.form.indicated.type = type;
      }
    },
    async openDetail(item, i) {
      this.idx = i;
      this.detail.loading = true;
      if (this.detail.collaborators.length === 0) {
        await this.loadCollaborators();
      }
      this.detail.data = item;
      this.detail.show = true;
      this.detail.loading = false;
    },
    openModalExpenses(expenses) {
      this.expenseModal.items = expenses;
      this.expenseModal.show = true;
    },
    eventDescription(event) {
      if (event.type === 'status') {
        return this.status.getDescription(event.value);
      }
      if (event.type === 'account') {
        if (event.value === 'created') {
          return 'Conta criada';
        }
        if (event.value === 'deleted') {
          return 'Conta excluída';
        }
      }
      return [event.type, event.value].join(' @ ');
    },
    dateFromNow(date) {
      return capitalize(moment(date).fromNow());
    },
    addSpecialty(specialty) {
      if (!specialty) {
        return;
      }
      this.specialty = {
        code: specialty.code,
        name: specialty.name,
      };
      this.form.indicated.specialty = {
        code: specialty.code,
        name: specialty.name,
      };
    },
    findSpecialty(search) {
      const params = {
        search,
        isSpecialty: true,
      };

      return this.$http.get('/services/cbos', { params })
        .then(({ data }) => data.items);
    },
    unsetSpecialty() {
      this.form.indicated.specialty = this.blankIndicatedForm().specialty;
      this.specialty = null;
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$error;
    },
    blankIndicatedForm() {
      return {
        id: '',
        name: '',
        type: 'professional',
        council: {
          name: 'CRM',
          state: 'PR',
          record: '',
        },
        specialty: {
          code: '',
          name: '',
        },
      };
    },
  },
};
</script>

<style lang="scss">
  @import 'src/assets/scss/variables';
  .history-modal {
    .load-more {
      margin: $layout-spacing-lg 0;
      text-align: center;
      .btn {
        padding: 0 $layout-spacing-xl;
      }
    }
    .empty {
      padding: $layout-spacing-xl;
      .empty-icon {
        margin-bottom: $layout-spacing-sm;
      }
      .empty-subtitle {
        margin: $layout-spacing-lg;
      }
    }
  }
</style>
